import React, {
	createContext,
	FC,
	ReactNode,
	useEffect,
	useMemo,
	useState,
	useCallback,
} from 'react';
import { openDatabase, storeSelectedColumns, getSelectedColumns } from './indexdbConfig';
import PropTypes from 'prop-types';

export interface IMetaDataProps {
	selectedColData: ISelectedColumns;
	addReportSelectedColumns: (columns: string[], name: string) => Promise<void>;
}

const MetaDataContext = createContext<IMetaDataProps>({} as IMetaDataProps);

interface IMetaDataProviderProps {
	children: ReactNode;
}

interface ISelectedColumns {
	[key: string]: string[];
}

export const MetaDataContextProvider: FC<IMetaDataProviderProps> = ({ children }) => {
	const [selectedColData, setSelectedColData] = useState<ISelectedColumns>({});

	// Function to add and store selected columns
	const addReportSelectedColumns = useCallback(async (columns: string[], name: string) => {
		openDatabase()
			.then((dbint) => {
				storeSelectedColumns(dbint, columns, name)
					.then(() => {
						setSelectedColData((prevData) => ({
							...prevData,
							[name]: columns,
						}));
					})
					.catch((error) => {
						console.error('Failed to store selected columns');
					});
			})
			.catch((error) => {
				console.error('Failed to open the database');
			});
	}, []);
	useEffect(() => {
		openDatabase()
			.then((dbint) => {
				getSelectedColumns(dbint)
					.then((data) => {
						// Ensure data is correctly typed as ISelectedColumns
						const typedData: ISelectedColumns = data.reduce((acc, item) => {
							acc[item.name] = item.columns;
							return acc;
						}, {} as ISelectedColumns);

						// Update the state with retrieved data from IndexedDB
						setSelectedColData(typedData || {});
					})
					.catch((error) => {
						console.error('Failed to get selected columns');
					});
			})
			.catch((error) => {
				console.error('Failed to open the database');
			});
	}, []);

	const value = useMemo(
		() => ({
			selectedColData,
			addReportSelectedColumns,
		}),
		[selectedColData, addReportSelectedColumns], // Make sure to include selectedColData in the dependencies
	);
	return <MetaDataContext.Provider value={value}>{children}</MetaDataContext.Provider>;
};

MetaDataContextProvider.propTypes = {
	children: PropTypes.node.isRequired,
};

export default MetaDataContext;
